import React, { memo, useState } from "react";
import { Box, Button, Fade, Modal, Typography } from "@mui/material";

import styles from "../../../components/common/DataTable/DataTable.module.css";
import ImgFrame from "../../../components/common/ImageFrame/ImgFrame";

import viewIcon from "../../../assets/images/svg/viewtech.svg";
import editIcon from "../../../assets/images/svg/File Edit.svg";
import dltIcon from "../../../assets/images/svg/Delete.svg";

const safe = (value) =>
  value === undefined || value === null || String(value).trim() === ""
    ? "-"
    : String(value);

const QuotationItemsTable = ({
  headings,
  rows,
  onView,
  onEdit,
  onDelete,
  showActions = true,
  stickyFirstColumn = true,
}) => {
  const [readMoreOpen, setReadMoreOpen] = useState(false);
  const [readMoreText, setReadMoreText] = useState("");

  const openReadMore = (text) => {
    setReadMoreText(String(text || "").trim());
    setReadMoreOpen(true);
  };

  return (
    <div className={styles.datatable_wrapper}>
      <table style={{ width: rows?.length ? "max-content" : "100%" }}>
        <thead>
          <tr>
            {headings.map((h) => (
              <th
                key={h.field}
                style={{
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor: "#e0e9ee",
                  ...(stickyFirstColumn && h === headings[0]
                    ? {
                        left: 0,
                        zIndex: 4,
                        boxShadow: "2px 0 0 0 #e2e8f0 inset",
                      }
                    : {}),
                }}
              >
                {h.label}
              </th>
            ))}
            {showActions && (
              <th
                style={{
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  right: 0,
                  zIndex: 3,
                  backgroundColor: "#e0e9ee",
                  boxShadow: "-2px 0 0 0 #e2e8f0 inset",
                }}
              >
                Action
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {!rows?.length ? (
            <tr>
              <td colSpan={headings.length + (showActions ? 1 : 0)}>
                <span className={styles.infoMsg}>
                  No data available.
                </span>
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.token || row.id || row.part_number || row.item_name}>
                {headings.map((h) => (
                  <td
                    key={h.field}
                    style={{
                      whiteSpace: "nowrap",
                      ...(stickyFirstColumn && h === headings[0]
                        ? {
                            position: "sticky",
                            left: 0,
                            zIndex: 2,
                            backgroundColor: "#f8fafc",
                            boxShadow: "2px 0 0 0 #e2e8f0 inset",
                          }
                        : {}),
                    }}
                  >
                    {h.cellType === "readMore"
                      ? (() => {
                          const fullText =
                            row[h.field] === null || row[h.field] === undefined
                              ? ""
                              : String(row[h.field]);
                          const maxLen =
                            typeof h.maxLength === "number" ? h.maxLength : 80;
                          const trimmed = fullText.trim();
                          const needsMore = trimmed.length > maxLen;
                          const shortText = needsMore
                            ? `${trimmed.slice(0, maxLen).trim()}...`
                            : trimmed || "-";

                          return (
                            <div
                              className={styles.readMoreCell}
                              style={{ maxWidth: h.col_width || "320px" }}
                            >
                              <span className={styles.readMoreText}>
                                {shortText}
                              </span>
                              {needsMore && (
                                <button
                                  type="button"
                                  className={styles.readMoreLink}
                                  onClick={() => openReadMore(trimmed)}
                                >
                                  Read more
                                </button>
                              )}
                            </div>
                          );
                        })()
                      : safe(row[h.field])}
                  </td>
                ))}
                {showActions && (
                  <td className={styles.action}>
                    {onView && (
                      <ImgFrame
                        imgSrc={viewIcon}
                        ImgClick={() => onView(row)}
                        customClass={styles.actBtn}
                      />
                    )}
                    {onEdit && (
                      <ImgFrame
                        imgSrc={editIcon}
                        ImgClick={() => onEdit(row)}
                        customClass={styles.actBtn}
                      />
                    )}
                    {onDelete && (
                      <ImgFrame
                        imgSrc={dltIcon}
                        ImgClick={() => onDelete(row)}
                        customClass={styles.actBtn}
                      />
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      <Modal open={readMoreOpen} onClose={() => setReadMoreOpen(false)}>
        <Fade in={readMoreOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(92vw, 780px)",
              maxHeight: "86vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: "14px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 24px 70px rgba(15, 23, 42, 0.2)",
              p: 2.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                mb: 1,
              }}
            >
              <Typography sx={{ fontFamily: "gilroyBold", fontSize: 18 }}>
                Description
              </Typography>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontFamily: "gilroyBold",
                  px: 3,
                  py: 1,
                  minWidth: 110,
                  fontSize: 14,
                }}
                onClick={() => setReadMoreOpen(false)}
              >
                Close
              </Button>
            </Box>
            <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 14 }}>
              {readMoreText || "-"}
            </Typography>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default memo(QuotationItemsTable);
